import { useState, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Trash2,
  Pencil,
  Check,
  X as XIcon,
  Loader2,
  Rss,
  ImagePlus,
  Eye,
  ExternalLink,
  Sparkles,
  Image,
  Languages,
  Users,
} from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import ExpandableMediaGrid from './ExpandableMediaGrid';
import Pagination from './Pagination';
import Spinner from './ui/spinner';
import { api } from '../services/client';
import { formatDateTime } from '../utils/formatDate';
import { getImageUrl } from '../utils/imageUrl';
import useDebouncedValue from '../hooks/useDebouncedValue';
import useConfirmDialog from '../hooks/useConfirmDialog';
import { useTranslation } from '../i18n';
import {
  useXPostsList,
  useXTopics,
  useXChannels,
  useCrawlStatus,
  useDeleteXPost,
  useTogglePublishXPost,
  useUpdateXPost,
  useCreateXPost,
  useTriggerCrawl,
  useClassifyXPost,
  useGenerateImage,
  useGenerateThumbnails,
  useTranslateImages,
  useCancelAllJobs,
  useSyncFollowings,
  xPostsKeys,
} from '../hooks/queries/useXPostsQueries';

const PAGE_LIMIT = 20;

const DEFAULT_TOPICS = [
  'politics',
  'economy',
  'tech',
  'sports',
  'entertainment',
  'other',
];

const XPostsManagement = () => {
  const { t } = useTranslation();
  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [editTopics, setEditTopics] = useState<string[]>([]);
  const [editMediaUrls, setEditMediaUrls] = useState<string[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createText, setCreateText] = useState('');
  const [createTopics, setCreateTopics] = useState<string[]>([]);
  const [createMediaUrls, setCreateMediaUrls] = useState<string[]>([]);
  const [overlayImage, setOverlayImage] = useState<string | null>(null);

  // URL search params for filters/pagination
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') || '';
  const topicFilter = searchParams.get('topic') || 'all';
  const statusFilter = searchParams.get('status') || 'all';
  const page = Number(searchParams.get('page')) || 1;

  const debouncedSearch = useDebouncedValue(search, 300);

  // --- TanStack Query hooks ---
  const {
    data: postsData,
    isLoading,
    isPlaceholderData,
  } = useXPostsList({
    page,
    limit: PAGE_LIMIT,
    search: debouncedSearch,
    topic: topicFilter,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  });
  const { data: availableTopics = DEFAULT_TOPICS } = useXTopics();
  const { data: channels = [] } = useXChannels();
  const { data: crawlStatus } = useCrawlStatus();

  const deletePost = useDeleteXPost();
  const togglePublish = useTogglePublishXPost();
  const updatePost = useUpdateXPost();
  const createPost = useCreateXPost();
  const triggerCrawl = useTriggerCrawl();
  const cancelAllJobs = useCancelAllJobs();
  const syncFollowings = useSyncFollowings();
  const classifyPost = useClassifyXPost();
  const generateImage = useGenerateImage();
  const generateThumbnails = useGenerateThumbnails();
  const translateImages = useTranslateImages();
  const confirmDialog = useConfirmDialog();
  const queryClient = useQueryClient();
  const isCrawling = triggerCrawl.isPending || crawlStatus?.isCrawling;
  const isNonProduction = import.meta.env.VITE_ENV !== 'production';

  const wasCrawlingRef = useRef(false);

  useEffect(() => {
    if (wasCrawlingRef.current && !isCrawling) {
      queryClient.invalidateQueries({ queryKey: xPostsKeys.lists() });
    }
    wasCrawlingRef.current = !!isCrawling;
  }, [isCrawling, queryClient]);

  const posts = postsData?.data ?? [];
  const total = postsData?.meta?.total ?? 0;
  const totalPages = postsData?.meta?.totalPages ?? 1;

  // --- URL param helpers ---
  const setSearch = useCallback(
    (value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        if (value) {
          next.set('search', value);
        } else {
          next.delete('search');
        }

        next.delete('page');

        return next;
      });
    },
    [setSearchParams],
  );

  const setPage = useCallback(
    (newPage: number) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        if (newPage > 1) {
          next.set('page', String(newPage));
        } else {
          next.delete('page');
        }

        return next;
      });
    },
    [setSearchParams],
  );

  const setFilter = useCallback(
    (key: string, value: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        if (value && value !== 'all') {
          next.set(key, value);
        } else {
          next.delete(key);
        }

        next.delete('page');

        return next;
      });
    },
    [setSearchParams],
  );

  // --- Handlers ---
  const handleDelete = (id: string) => {
    confirmDialog.show(t('x_posts.delete_confirm'), async () => {
      deletePost.mutate(id);
    });
  };

  const handleTogglePublish = (post: {
    id: string;
    holdReason: string | null;
  }) => {
    togglePublish.mutate({ id: post.id, holdReason: post.holdReason });
  };

  const handleStartEdit = (post: {
    id: string;
    channelId: string | null;
    translatedText: string | null;
    topics: string[] | null;
    mediaUrls: string[] | null;
  }) => {
    setEditingId(post.id);
    setEditText(post.translatedText || '');
    setEditTopics(post.topics || []);
    setEditMediaUrls(post.mediaUrls || []);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
    setEditTopics([]);
    setEditMediaUrls([]);
  };

  const handleSaveEdit = () => {
    if (!editingId) return;

    if (!editText.trim() && editMediaUrls.length === 0) {
      toast.error(t('x_posts.content_required'));
      return;
    }

    if (editTopics.length === 0) {
      toast.error(t('x_posts.topic_required'));
      return;
    }

    const editingPost = posts.find((p) => p.id === editingId);
    const isManualPost = !editingPost?.channelId;

    updatePost.mutate(
      {
        id: editingId,
        data: {
          ...(isManualPost && { originalText: editText || undefined }),
          translatedText: editText || null,
          topics: editTopics,
          mediaUrls: editMediaUrls.length > 0 ? editMediaUrls : null,
        },
      },
      { onSuccess: () => setEditingId(null) },
    );
  };

  const selectTopic = (
    topic: string,
    setter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    setter((prev) => (prev.includes(topic) ? [] : [topic]));
  };

  const uploadImages = async (
    files: FileList,
    setter: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    for (const file of Array.from(files)) {
      try {
        const res = await api.upload<{ imageKey: string }>(
          '/backoffice/upload-image',
          file,
        );
        setter((prev) => [...prev, res.imageKey]);
      } catch {
        toast.error(t('x_posts.toast.upload_failed', { name: file.name }));
      }
    }
  };

  const handleDeleteEditImage = (index: number) => {
    setEditMediaUrls((prev) => prev.filter((_, i) => i !== index));
  };

  // Channel management handlers — hidden for now
  // const handleAddChannel = (e: React.FormEvent) => {
  //   e.preventDefault();
  //   const username = newChannel.trim().replace('@', '');
  //   if (!username) return;
  //   addChannel.mutate(username, { onSuccess: () => setNewChannel('') });
  // };

  // const handleRemoveChannel = (id: string, username: string) => {
  //   if (!confirm(t('x_posts.remove_channel_confirm', { username }))) return;
  //   removeChannel.mutate({ id, username });
  // };

  // const handleCrawlChannel = (id: string) => {
  //   triggerCrawl.mutate(id);
  // };

  const handleFetchPosts = () => {
    triggerCrawl.mutate(undefined);
  };

  const handleCreatePost = (e: React.FormEvent) => {
    e.preventDefault();

    if (!createText.trim() && createMediaUrls.length === 0) {
      return;
    }

    if (createTopics.length === 0) {
      toast.error(t('x_posts.topic_required'));
      return;
    }

    createPost.mutate(
      {
        originalText: createText,
        translatedText: createText,
        topics: createTopics,
        mediaUrls: createMediaUrls.length > 0 ? createMediaUrls : undefined,
      },
      {
        onSuccess: () => {
          setCreateText('');
          setCreateTopics([]);
          setCreateMediaUrls([]);
          setShowCreateForm(false);
        },
      },
    );
  };

  if (isLoading && posts.length === 0) return <Spinner />;

  return (
    <div className="p-4 lg:p-8 flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {t('x_posts.title')}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('x_posts.subtitle', { total, channels: channels.length })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-black flex items-center gap-2"
          >
            {t('x_posts.create_post')}
          </button>
          <button
            onClick={() => syncFollowings.mutate()}
            disabled={syncFollowings.isPending}
            className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-black disabled:opacity-50 flex items-center gap-2"
          >
            {syncFollowings.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Users className="w-4 h-4" />
            )}
            {syncFollowings.isPending
              ? t('x_posts.syncing_followings')
              : t('x_posts.sync_followings')}
          </button>
          <button
            onClick={handleFetchPosts}
            disabled={isCrawling}
            className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isCrawling ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Rss className="w-4 h-4" />
            )}
            {isCrawling
              ? t('x_posts.fetching_posts')
              : t('x_posts.fetch_posts')}
          </button>
          {isCrawling && isNonProduction && (
            <button
              onClick={() => cancelAllJobs.mutate()}
              disabled={cancelAllJobs.isPending}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              <XIcon className="w-4 h-4" />
              {t('x_posts.cancel_jobs')}
            </button>
          )}
          {isNonProduction && (
            <button
              onClick={() => generateThumbnails.mutate()}
              disabled={generateThumbnails.isPending}
              className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-black disabled:opacity-50 flex items-center gap-2"
            >
              {generateThumbnails.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Image className="w-4 h-4" />
              )}
              {t('x_posts.generate_thumbnails')}
            </button>
          )}
        </div>
      </div>

      {/* Channel Management — hidden for now */}

      {/* Create Post Form */}
      {showCreateForm && (
        <Card className="p-4">
          <form onSubmit={handleCreatePost} className="space-y-3">
            <Textarea
              value={createText}
              onChange={(e) => setCreateText(e.target.value)}
              placeholder={t('x_posts.content_placeholder')}
              rows={3}
              className="text-sm"
            />
            <div className="flex flex-wrap gap-2">
              {availableTopics.map((topic) => (
                <button
                  key={topic}
                  type="button"
                  onClick={() => selectTopic(topic, setCreateTopics)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                    createTopics.includes(topic)
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t(`x_posts.topic.${topic}`)}
                </button>
              ))}
            </div>
            {/* Image upload */}
            <div>
              <label className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200">
                <ImagePlus className="w-3.5 h-3.5" />
                {t('x_posts.upload')}
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files) {
                      uploadImages(e.target.files, setCreateMediaUrls);
                    }
                  }}
                />
              </label>
              {createMediaUrls.length > 0 && (
                <div className="flex gap-2 mt-2">
                  {createMediaUrls.map((url, i) => (
                    <div key={i} className="relative">
                      <img
                        src={getImageUrl(url)}
                        alt=""
                        className="w-16 h-16 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setCreateMediaUrls((prev) =>
                            prev.filter((_, idx) => idx !== i),
                          )
                        }
                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white rounded-full text-[10px] flex items-center justify-center"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={
                  (!createText.trim() && createMediaUrls.length === 0) ||
                  createPost.isPending
                }
                className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-lg hover:bg-black disabled:opacity-50"
              >
                {createPost.isPending
                  ? t('x_posts.creating')
                  : t('x_posts.create_btn')}
              </button>
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                {t('x_posts.cancel')}
              </button>
            </div>
          </form>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <div className="p-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('x_posts.search_placeholder')}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              {[
                { value: 'all', label: t('common.all') },
                { value: 'posted', label: t('x_posts.status.posted') },
                { value: 'held', label: t('x_posts.status.held') },
              ].map((item) => (
                <Button
                  key={item.value}
                  variant={
                    statusFilter === item.value ||
                    (item.value === 'all' && !statusFilter)
                      ? 'default'
                      : 'outline'
                  }
                  onClick={() => setFilter('status', item.value)}
                >
                  {item.label}
                </Button>
              ))}
            </div>
            <Select
              value={topicFilter}
              onValueChange={(val) => setFilter('topic', val)}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder={t('x_posts.all_topics')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('x_posts.all_topics')}</SelectItem>
                {availableTopics.map((topic) => (
                  <SelectItem key={topic} value={topic}>
                    {t(`x_posts.topic.${topic}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* Posts Grid */}
      <Card>
        <div className="p-6">
          <div
            className={`grid grid-cols-1 gap-4 ${isPlaceholderData ? 'opacity-60' : ''}`}
          >
            {posts.length === 0 && !isLoading ? (
              <Card className="p-12 text-center text-gray-500">
                {t('x_posts.no_posts')}
              </Card>
            ) : (
              posts.map((post) => {
                const isEditing = editingId === post.id;

                return (
                  <Card
                    key={post.id}
                    className={`p-4 transition-shadow ${isEditing ? 'ring-2 ring-blue-500 shadow-md' : 'hover:shadow-md'}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-gray-400">
                            {formatDateTime(post.publishedAt)}
                          </span>
                          {post.holdReason === 'error' && (
                            <span className="text-xs font-bold text-white bg-red-500 px-1.5 py-0.5 rounded">
                              {t('x_posts.hold_reason.error')}
                            </span>
                          )}
                          {post.holdReason === 'sensitive' && (
                            <span className="text-xs font-bold text-white bg-orange-500 px-1.5 py-0.5 rounded">
                              {t('x_posts.hold_reason.sensitive')}
                            </span>
                          )}
                          {post.holdReason && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-gray-200">
                              {t('x_posts.hold_reason.unposted')}
                            </span>
                          )}
                          {!post.holdReason && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-600 border border-green-200">
                              {t('x_posts.status.posted')}
                            </span>
                          )}
                        </div>

                        {/* Original text (read-only for crawled posts) */}
                        {!isEditing && post.channelId && (
                          <p className="text-sm text-gray-700 whitespace-pre-wrap mb-2">
                            {post.originalText}
                          </p>
                        )}

                        {/* Content — editable */}
                        {isEditing ? (
                          <div className="mb-3 space-y-2">
                            <label className="text-xs font-medium text-gray-600">
                              {post.channelId
                                ? t('x_posts.summary_label')
                                : t('x_posts.content_label')}
                            </label>
                            <Textarea
                              value={editText}
                              onChange={(e) => setEditText(e.target.value)}
                              placeholder={t('x_posts.content_placeholder')}
                              rows={3}
                              className="text-sm"
                            />
                          </div>
                        ) : (
                          <>
                            {post.translatedText && (
                              <div className="bg-blue-50 rounded-md px-3 py-2 mb-2">
                                <p className="text-xs text-blue-800">
                                  📝 {post.translatedText}
                                </p>
                              </div>
                            )}
                          </>
                        )}

                        {/* Media images */}
                        {isEditing ? (
                          <div className="mb-2">
                            {editMediaUrls.length > 0 && (
                              <div
                                className={`${editMediaUrls.length === 1 ? '' : 'grid grid-cols-2 gap-2'} max-w-lg mb-2`}
                              >
                                {editMediaUrls.map((url, idx) => (
                                  <div key={idx} className="relative group">
                                    <img
                                      src={getImageUrl(url)}
                                      alt={`Media ${idx + 1}`}
                                      className="rounded-md w-full object-cover border border-gray-200"
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteEditImage(idx)}
                                      className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                      title={t('x_posts.delete_title')}
                                    >
                                      ×
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <label className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg cursor-pointer hover:bg-gray-200">
                                <ImagePlus className="w-3.5 h-3.5" />
                                {t('x_posts.upload')}
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  className="hidden"
                                  onChange={(e) => {
                                    if (e.target.files) {
                                      uploadImages(
                                        e.target.files,
                                        setEditMediaUrls,
                                      );
                                    }
                                  }}
                                />
                              </label>
                            </div>
                          </div>
                        ) : (
                          post.mediaUrls &&
                          post.mediaUrls.length > 0 && (
                            <ExpandableMediaGrid
                              mediaUrls={post.mediaUrls}
                              onImageClick={setOverlayImage}
                            />
                          )
                        )}

                        {/* Topics — edit mode */}
                        {isEditing && (
                          <div className="mb-3 space-y-2">
                            <label className="text-xs font-medium text-gray-600">
                              {t('x_posts.topics_label')}
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {availableTopics.map((topic) => (
                                <button
                                  key={topic}
                                  onClick={() =>
                                    selectTopic(topic, setEditTopics)
                                  }
                                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                                    editTopics.includes(topic)
                                      ? 'bg-blue-500 text-white'
                                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                  }`}
                                >
                                  {t(`x_posts.topic.${topic}`)}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Topics & Engagement */}
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            {!isEditing &&
                              post.topics?.map((topic) => (
                                <Badge
                                  key={topic}
                                  variant="outline"
                                  className="text-[10px] bg-white text-gray-600 font-normal"
                                >
                                  {t(`x_posts.topic.${topic}`)}
                                </Badge>
                              ))}
                          </div>
                          {!post.xPostId.startsWith('manual-') && (
                            <div className="flex justify-end mt-1">
                              <a
                                href={`https://x.com/${post.authorUsername}/status/${post.xPostId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-blue-500 transition-colors"
                              >
                                <ExternalLink className="w-3 h-3" />
                                {t('x_posts.view_original')}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="shrink-0 flex flex-col gap-1">
                        {isEditing ? (
                          <>
                            <button
                              onClick={handleSaveEdit}
                              disabled={updatePost.isPending}
                              className="p-1.5 rounded hover:bg-green-100 text-green-600 disabled:opacity-50"
                              title={t('x_posts.save_title')}
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-400"
                              title={t('x_posts.cancel_title')}
                            >
                              <XIcon className="w-4 h-4" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleStartEdit(post)}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-500"
                              title={t('x_posts.edit_title')}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleTogglePublish(post)}
                              className={`p-1.5 rounded hover:bg-gray-100 ${!post.holdReason ? 'text-green-500 hover:text-orange-500' : 'text-gray-300 hover:text-green-500'}`}
                              title={
                                !post.holdReason
                                  ? t('x_posts.unpublish_title')
                                  : t('x_posts.publish_title')
                              }
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {isNonProduction && (
                              <>
                                <button
                                  onClick={() => classifyPost.mutate(post.id)}
                                  disabled={classifyPost.isPending}
                                  className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-purple-500"
                                  title={t('x_posts.classify_title')}
                                >
                                  <Sparkles className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => generateImage.mutate(post.id)}
                                  disabled={generateImage.isPending}
                                  className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-indigo-500"
                                  title={t('x_posts.generate_image_title')}
                                >
                                  <Image className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    translateImages.mutate(post.id)
                                  }
                                  disabled={
                                    translateImages.isPending ||
                                    !post.mediaUrls?.length
                                  }
                                  className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-teal-500 disabled:opacity-30 disabled:cursor-not-allowed"
                                  title={t('x_posts.translate_images_title')}
                                >
                                  <Languages className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => handleDelete(post.id)}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-red-500"
                              title={t('x_posts.delete_title')}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && total > PAGE_LIMIT && (
            <Pagination
              page={page}
              totalPages={totalPages}
              total={total}
              limit={PAGE_LIMIT}
              onPageChange={setPage}
            />
          )}
        </div>
      </Card>

      {/* Image overlay */}
      {overlayImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          onClick={() => setOverlayImage(null)}
        >
          <button
            type="button"
            onClick={() => setOverlayImage(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <XIcon className="w-6 h-6" />
          </button>
          <img
            src={overlayImage}
            alt="Full size"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Confirm Dialog */}
      <Dialog
        open={confirmDialog.isOpen}
        onOpenChange={(open) => !open && confirmDialog.cancel()}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle>{t('common.confirm')}</DialogTitle>
            <DialogDescription>{confirmDialog.message}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={confirmDialog.cancel}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDialog.confirm}
              disabled={confirmDialog.isLoading}
            >
              {confirmDialog.isLoading
                ? t('common.saving')
                : t('common.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default XPostsManagement;
